<template>
  <div class="reporting-table">
    <div class="reporting-table-toolbar">
      <div class="reporting-toolbar px-i">
        <div v-if="showNewButton"
             class="reporting-toolbar-wrapper w-sm-100">
          <div class="reporting-toolbar-wrapper-button">
            <a
              class="btn btn-primary ihjoz-button"
              :href="newButtonUrl">{{ newButtonTitle }}</a>
          </div>
        </div>
        <div v-if="enableDropdown"
             class="reporting-toolbar-wrapper w-xs-100">
          <select
            class="custom-select custom-input reporting-toolbar-wrapper-select"
            @change="dropdownChanged($event)">
            <option
              v-for="option in dropdownOptions"
              :key="Object.keys(option)[0]"
              :value="Object.values(option)[0]">
              {{ Object.keys(option)[0] }}
            </option>
          </select>
        </div>
        <div class="reporting-toolbar-wrapper">
          <div class="reporting-toolbar-wrapper-group">
            <a v-if="showFormToggle"
               href="#reporting-filters"
               class="reporting-toolbar-wrapper-toggle"
               data-toggle="modal">
              <i class="demo-icon icon-filter" />
            </a>
            <a
              href="#toggle-columns"
              data-toggle="modal"
              class="reporting-toolbar-wrapper-toggle">
              <i class="demo-icon icon-sliders-h" />
            </a>
            <button
              type="button"
              class="reporting-toolbar-wrapper-toggle"
              :disabled="currentItems.length == 0"
              @click="exportReport()">
              <i class="demo-icon icon-cloud-download" />
            </button>
          </div>
        </div>
        <div class="reporting-toolbar-wrapper">
          <div class="reporting-toolbar-count">
            <div class="reporting-toolbar-count-icon">
              <i class="demo-icon icon-receipt" />
            </div>
            <div class="reporting-toolbar-count-text">
              <p class="reporting-toolbar-count-text-number">
                {{ totalRows }}
              </p>
              <p class="reporting-toolbar-count-text-unit mb-0">
                {{ itemsCountTitle }}
              </p>
            </div>
          </div>
        </div>
        <div class="reporting-toolbar-wrapper reporting-toolbar-wrapper-searchbar">
          <div class="input-group reporting-toolbar-search">
            <input
              type="text"
              class="form-control reporting-toolbar-search-input"
              :placeholder="$t('common.search')"
              @input="onFilterEnter($event)">
            <div class="input-group-append reporting-toolbar-search-append">
              <i class="demo-icon icon-search" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="toggle-columns"
         class="modal fade"
         tabindex="-1"
         role="dialog"
         aria-labelledby="reporting-columns-modal-label"
         aria-hidden="true">
      <div class="modal-dialog custom-modal reporting-columns">
        <div class="modal-content">
          <div class="modal-header custom-modal-header px-i">
            <div class="custom-modal-header-start">
              <h4 class="modal-title custom-modal-header-start-title">
                {{ $t('common.tableSettings') }}
              </h4>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div class="modal-body custom-modal-body p-0">
            <div
              v-for="field in filterColumns"
              :key="field.key"
              class="reporting-columns-filter px-i">
              <div class="reporting-columns-filter-start">
                <div class="reporting-columns-filter-start-text">
                  {{ field.filterLabel }}
                </div>
              </div>
              <div class="reporting-columns-filter-end">
                <div class="ihjoz-toggle">
                  <input
                    :id="`filter-${field.key}`"
                    type="checkbox"
                    :checked="fieldVisibility[field.key]"
                    class="ihjoz-toggle-input"
                    @change="hideShowColumn($event, field.key)">
                  <label class="ihjoz-toggle-button" :for="`filter-${field.key}`">
                    <i class="demo-icon icon-eye-slash" />
                    <i class="demo-icon icon-eye" />
                    <span class="ihjoz-toggle-button-slider" />
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer custom-modal-footer px-i">
            <button type="button"
                    class="btn btn-primary ihjoz-button ihjoz-button-big-size"
                    data-dismiss="modal"
                    aria-label="Close">
              {{ $t('common.done') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="reporting-table-data">
      <fieldset class="table-wrapper" :disabled="isFormDisabled">
        <div :class="'table-wrapper-data table-wrapper-data-' + perPage">
          <b-table
            ref="table"
            show-empty
            hover
            borderless
            :items="items"
            :fields="fields"
            :busy="isBusy"
            :per-page="perPage"
            :current-page="currentPage"
            :filter="filter"
            thead-class="data-table-header"
            thead-tr-class="data-table-header-row"
            tbody-class="data-table-body"
            tbody-tr-class="data-table-body-row px-i"
            class="data-table"
            :filter-function="filterFunction"
            @filtered="onFiltered">
            <template v-for="field in fields" v-slot:[`head(${field.key})`]="data">
              <span>{{ data.label }}</span>
              <i v-if="field.sortable" class="demo-icon icon-sort"></i>
            </template>
            <template v-slot:cell()="data">
              <div class="column-text-wrapper" :class="data.field.customClass">
                {{ data.value }}
              </div>
            </template>

            <template v-slot:cell(link)="data">
              <slot name="cell(link)" :data="data" />
            </template>

            <template v-for="slot in customFormattedColumns" v-slot:[slotName(slot)]="data">
              <slot :name="slotName(slot)" :data="data" />
            </template>

            <template
              v-if="showTotalRow"
              v-slot:bottom-row="data">
              <td
                v-for="field in data.fields"
                :key="field[loopingKey]"
                :class="visibilityClass(field.key)">
                <strong v-if="shouldCalculateTotal(field)" :class="field.customClass">
                  {{ total(field) }}
                </strong>
              </td>
            </template>

            <template v-slot:table-busy>
              <div class="table-busy">
                <div class="table-busy-wrapper">
                  <div class="table-busy-wrapper-spinner">
                    <div />
                    <div />
                    <div />
                    <div />
                  </div>
                  <div class="table-busy-wrapper-text my-2">
                    {{ $t('common.loading') }}
                  </div>
                </div>
              </div>
            </template>
          </b-table>
        </div>
        <div class="table-wrapper-footer px-i">
          <div class="table-wrapper-footer-count">
            <select
              v-model="perPage"
              class="form-control custom-select table-wrapper-footer-count-input">
              <option
                v-for="perPageOption in [10, 25, 50, 100]"
                :key="perPageOption"
                :value="perPageOption">
                {{ perPageOption }} per page
              </option>
            </select>
          </div>

          <div class="table-wrapper-footer-pagination">
            <b-pagination
              v-model="currentPage"
              small
              :total-rows="totalRows"
              :per-page="perPage"
              class="table-pagination">
              <span slot="first-text">{{ $t('pagination.first') }}</span>
              <span slot="prev-text">
                <i class="demo-icon icon-chevron-left" />
              </span>
              <span slot="next-text">
                <i class="demo-icon icon-chevron-right" />
              </span>
              <span slot="last-text">{{ $t('pagination.last') }}</span>
            </b-pagination>
          </div>
        </div>
      </fieldset>
    </div>
  </div>
</template>

<script>
import { capitalize, pluralize } from "@helpers";
import { BTable, BPagination } from "bootstrap-vue";

import createSearchRegexp from "@shared/createSearchRegexp";
import excelExport from "@shared/excelExport";
import { titleize, formatDecimal, formatPercentage } from "../../../helpers";

let searchRegexp = null;

export default {
  name: "ReportTable",

  components: {
    BTable,
    BPagination
  },

  props: {
    columns: { type: Array, required: true },
    itemName: { type: String, default: "item" },
    items: { type: Array, required: true },
    newButtonTitle: { type: String, default: "" },
    newButtonUrl: { type: String, default: "" },
    extraButtonTitle: { type: String, default: "" },
    extraButtonUrl: { type: String, default: "" },
    customFormattedColumns: { type: Array },
    showTotalRow: { type: Boolean, default: false },
    loopingKey: { type: String, default: "id" },
    totalCurrency: { type: Object },
    exportFilename: { type: String, default: "" },
    showFormToggle: { type: Boolean, default: true },
    enableDropdown: { type: Boolean, default: false },
    dropdownOptions: { type: Array }
  },

  data () {
    return {
      filteredItems: [],
      isFormDisabled: false,
      currentPage: 1,
      perPage: 10,
      isBusy: false,
      filter: "",
      fieldVisibility: {}
    };
  },

  computed: {
    showNewButton () {
      return this.newButtonTitle.length > 0;
    },

    totalRows () {
      return this.currentItems.length;
    },

    currentItems () {
      return this.filter.length > 0 ? this.filteredItems : this.items;
    },

    filterColumns () {
      return this.fields.filter(function (f) {
        return f.filterLabel;
      });
    },

    fields () {
      return this.columns.map((c) => ({
        ...c,
        tdClass: this.visibilityClass(c.key),
        thClass: this.visibilityClass(c.key)
      }));
    },

    searchableProperties () {
      return this.columns.reduce((result, e) => {
        if (e.searchable) { result.push(e.key); }

        return result;
      }, []);
    },

    itemsCountTitle () {
      return capitalize(pluralize(this.itemName, this.totalRows));
    },

    fieldVisibilityExceptLink () {
      return this.$_.omit(this.fieldVisibility, "link");
    }
  },

  watch: {
    filter (val) {
      searchRegexp = createSearchRegexp(val, false, true, true);
    }
  },

  mounted () {
    this.initFieldVisibility();
  },

  created () {
    this.onFilterEnter = this.$_.debounce(function (e) {
      this.isBusy = true;
      setTimeout(() => {
        this.filter = e.target.value;
      });
    }.bind(this), 250);
  },

  methods: {
    exportReport () {
      let date = new Date().toJSON().slice(0, 10);

      excelExport(
        this.rowHeaders(),
        this.rows(),
        `${this.exportFilename}-${date}.xlsx`,
        {
          title: `${Dashboard.currentTenant.name} Report`,
          subject: `${Dashboard.currentTenant.name} Report`,
          author: `${Dashboard.currentTenant.name}`
        }
      );
    },

    rowHeaders () {
      let result = [];

      for (const key of Object.keys(this.fieldVisibilityExceptLink)) {
        if (this.fieldVisibility[key]) {
          result.push(this.columns.find((e) => e.key === key).label);
        }
      }

      return this.$_.compact(result);
    },

    rows () {
      let visibleColumns = [];

      for (const key of Object.keys(this.fieldVisibilityExceptLink)) {
        if (this.fieldVisibility[key]) {
          visibleColumns.push(key);
        }
      }

      return this.currentItems.map((e) => this.$_.values(this.$_.pick(e, visibleColumns)));
    },

    initFieldVisibility () {
      let visibilityKey =
        window.innerWidth < 768 ? "visibleSmallScreen" : "visible";

      this.fieldVisibility = this.columns.reduce(
        (result, e) => ({ ...result, [e.key]: e[visibilityKey] }),
        {}
      );
    },

    hideShowColumn (e, key) {
      this.fieldVisibility[key] = !this.fieldVisibility[key];
    },

    onFiltered (filteredItems) {
      this.currentPage = 1;

      this.filteredItems = filteredItems;
      this.isBusy = false;
    },

    filterFunction (record, filter) {
      return searchRegexp.test(this.searchableProperties.map((e) => record[e]).join(""));
    },

    visibilityClass (fieldName) {
      return this.fieldVisibility[fieldName] ? "" : "d-none";
    },

    slotName (name) {
      return `cell(${name})`;
    },

    formatCellDate (value, key, item) {
      if (!value || (value && value.length === 0)) {
        return "—";
      }

      let date = new Date(value);

      return date.toLocaleDateString(
        undefined,
        { month: "short", day: "numeric", year: "numeric" }
      );
    },

    formatCellDateTime (value, key, item) {
      if (!value || (value && value.length === 0)) {
        return "—";
      }

      let date = new Date(value);
      let formattedDate = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      let formattedTime = date.toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit" });

      return `${formattedDate} ${formattedTime}`;
    },

    formatCurrency (value, key, item) {
      if (value) {
        let currencyMinimumDigits;

        if (item.invoice_currency_iso_code === "LBP") {
          currencyMinimumDigits = 0;
        } else {
          currencyMinimumDigits = 2;
        }

        return formatDecimal(value, currencyMinimumDigits);
      } else {
        return "—";
      }
    },

    titleized (value, key, item) {
      return titleize(value);
    },

    upCase (value, key, item) {
      if (value) {
        return value.toUpperCase();
      } else {
        return "—";
      }
    },

    formatPercentage (value, key, item) {
      if (value) {
        return formatPercentage(value);
      } else {
        return "—";
      }
    },

    shouldCalculateTotal (field) {
      return field.total;
    },

    total (field) {
      let total = this.currentItems.reduce((prev, next) => +prev + +next[field.key], 0);

      if (field.notCurrency) {
        return total;
      } else {
        return formatDecimal(total, this.totalCurrency.precision);
      }
    },

    dropdownChanged (e) {
      this.$emit("dropdownChanged", e.target.value);
    }
  }
};
</script>
